import React from 'react';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';

/**
 * Drop target styling, shared by every upload field.
 *
 * Takes the props object from react-dropzone's useDropzone() rather than
 * calling the hook itself, so each screen keeps control of its own accept
 * rules and drop handler.
 */
const Dropzone = ({ label, hint, dropzone, prompt = 'Drag files here, or tap to browse', children }) => {
  const { getRootProps, getInputProps, isDragActive } = dropzone;

  return (
    <div>
      {label && <span className="mb-1.5 block text-sm font-medium text-foreground">{label}</span>}

      <div
        {...getRootProps()}
        className={[
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-8 text-center',
          'transition-colors duration-base',
          isDragActive
            ? 'border-accent bg-accent-soft'
            : 'border-border-strong bg-muted/50 hover:border-accent hover:bg-accent-soft',
        ].join(' ')}
      >
        <input {...getInputProps()} />
        <CloudUploadOutlined
          style={{ fontSize: 32 }}
          aria-hidden="true"
          className="mb-2 text-muted-fg"
        />
        <p className="text-sm font-medium text-foreground">
          {isDragActive ? 'Drop the files here' : prompt}
        </p>
        {hint && <p className="mt-1 text-xs text-muted-fg">{hint}</p>}
      </div>

      {children}
    </div>
  );
};

/** Row for an already-attached file. */
export const FileRow = ({ icon: Icon, name, onRemove, children }) => (
  <li className="rounded-md border border-border bg-surface px-3 py-2">
    <div className="flex items-center gap-3">
      {Icon && <Icon style={{ fontSize: 20 }} aria-hidden="true" className="shrink-0 text-muted-fg" />}
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-fg transition-colors duration-fast hover:bg-danger-soft hover:text-danger"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
    {children}
  </li>
);

export default Dropzone;
